/**
 * The workflow builder's graph rules, kept out of React so they can be
 * tested as plain functions.
 *
 * A WORKFLOW IS STEPS AND ARROWS (ADR-276). Each step has at most one default
 * arrow and any number of arrows before it that are taken only on named
 * endings (`when.outcome`). Every edit here keeps that shape: branches first,
 * the default last, and no arrow that leads back to a step the call has
 * already passed — the builder only ever offers steps later in the call, and
 * each step retries inside itself.
 *
 * What a caller can do during ANY step (ask for a person, ask for a callback,
 * end the call) is not drawn as arrows: it is set once, on the workflow
 * (`anyTime`, `close`), and compiled onto every step that allows it.
 */

import { configAt, withConfigAt } from '@vagusx/agent-core/v2/config-path';
import type {
  AgentComponent,
  ComponentCallDirection,
  ComponentChannel,
  ComponentField,
  DecideConfig,
  DecisionPreset,
  LineContent,
  WorkflowGraph,
  WorkflowNode,
  WorkflowProblem,
  WorkflowStep,
} from '@vagusx/clients';

// ── Endings ──────────────────────────────────────────────────────────────────

/** One way a step can end that an arrow may be drawn for. */
export interface StepExit {
  id: string;
  label: string;
  /** The call is over after this ending on purpose (a transfer): it needs no arrow. */
  ends?: boolean;
}

/** The ending a Decide step takes when none of its answers holds. */
export const DECIDE_OTHERWISE = 'otherwise';

/** The question a Decide step asks: its preset's, or the one written into it. */
export function decisionOf(
  config: Record<string, unknown>,
  presets: readonly DecisionPreset[]
): DecideConfig | null {
  if (typeof config.preset === 'string') {
    return presets.find((p) => p.key === config.preset)?.config ?? null;
  }
  const answers = config.answers;
  if (typeof config.question !== 'string' || !Array.isArray(answers)) return null;
  return {
    question: config.question,
    answers: answers.filter(
      (a): a is DecideConfig['answers'][number] =>
        typeof a === 'object' && a !== null && typeof (a as { id?: unknown }).id === 'string'
    ),
    otherwiseLabel: typeof config.otherwiseLabel === 'string' ? config.otherwiseLabel : 'Otherwise',
  };
}

/**
 * The endings an author may route. Left out: the three a shared tool gives
 * every step (asking for a person, a callback, hanging up), which the
 * workflow's any-time rules handle once for all steps.
 */
export function stepExits(
  step: Pick<WorkflowStep, 'componentKey' | 'config'> | undefined,
  component: AgentComponent | undefined,
  presets: readonly DecisionPreset[]
): StepExit[] {
  if (!step || !component) return [];
  if (component.key === 'Decide') {
    const decision = decisionOf(step.config, presets);
    if (!decision) return [];
    return [
      ...decision.answers.map((a) => ({ id: a.id, label: a.label })),
      { id: DECIDE_OTHERWISE, label: decision.otherwiseLabel },
    ];
  }
  return component.meta.outcomes
    .filter((o) => !o.shared && !o.terminal)
    .map((o) => ({ id: o.id, label: o.label, ...(o.ends ? { ends: true } : {}) }));
}

/**
 * Every ending a step declares, the any-time ones included, for putting a
 * name to an arrow. An author may route one of those by hand on a single step
 * (the inbound front desk sends "asked for a callback" to identity first), and
 * that arrow still needs a label in words.
 */
export function allExits(
  step: Pick<WorkflowStep, 'componentKey' | 'config'> | undefined,
  component: AgentComponent | undefined,
  presets: readonly DecisionPreset[]
): StepExit[] {
  if (!component || component.key === 'Decide') return stepExits(step, component, presets);
  return component.meta.outcomes.map((o) => ({ id: o.id, label: o.label }));
}

// ── Arrows ───────────────────────────────────────────────────────────────────

export interface Branch {
  outcomes: string[];
  to: string;
}

/** Where a step goes: its branches in order, and its default. */
export interface NextSteps {
  branches: Branch[];
  otherwise: string | null;
}

export function nextOf(node: WorkflowNode): NextSteps {
  const branches: Branch[] = [];
  let otherwise: string | null = null;
  for (const t of node.transitions) {
    if (t.when) branches.push({ outcomes: [...t.when.outcome], to: t.to });
    else if (otherwise === null) otherwise = t.to;
  }
  return { branches, otherwise };
}

/** The node with its arrows rebuilt: branches in order, then the default. */
function rebuilt(node: WorkflowNode, next: NextSteps): WorkflowNode {
  return {
    ...node,
    transitions: [
      ...next.branches
        .filter((b) => b.outcomes.length > 0)
        .map((b) => ({ to: b.to, when: { outcome: [...b.outcomes] } })),
      ...(next.otherwise ? [{ to: next.otherwise }] : []),
    ],
  };
}

function withNext(graph: WorkflowGraph, nodeId: string, next: NextSteps): WorkflowGraph {
  return { ...graph, nodes: graph.nodes.map((n) => (n.id === nodeId ? rebuilt(n, next) : n)) };
}

function nodeOf(graph: WorkflowGraph, id: string): WorkflowNode | undefined {
  return graph.nodes.find((n) => n.id === id);
}

function editNext(
  graph: WorkflowGraph,
  nodeId: string,
  edit: (next: NextSteps) => NextSteps
): WorkflowGraph {
  const node = nodeOf(graph, nodeId);
  return node ? withNext(graph, nodeId, edit(nextOf(node))) : graph;
}

export function setOtherwise(
  graph: WorkflowGraph,
  nodeId: string,
  to: string | null
): WorkflowGraph {
  return editNext(graph, nodeId, (next) => ({ ...next, otherwise: to }));
}

export function setBranchTarget(
  graph: WorkflowGraph,
  nodeId: string,
  index: number,
  to: string
): WorkflowGraph {
  return editNext(graph, nodeId, (next) => ({
    ...next,
    branches: next.branches.map((b, i) => (i === index ? { ...b, to } : b)),
  }));
}

/**
 * Send one more ending somewhere of its own. Joins the branch already going
 * there, if there is one, so two endings to one step are one arrow.
 */
export function addBranch(
  graph: WorkflowGraph,
  nodeId: string,
  outcome: string,
  to: string
): WorkflowGraph {
  return editNext(graph, nodeId, (next) => {
    const branches = next.branches.map((b) => ({
      ...b,
      outcomes: b.outcomes.filter((o) => o !== outcome),
    }));
    const joined = branches.find((b) => b.to === to);
    if (joined) joined.outcomes.push(outcome);
    else branches.push({ outcomes: [outcome], to });
    return { ...next, branches: branches.filter((b) => b.outcomes.length > 0) };
  });
}

/** Stop sending an ending (or a whole branch) its own way: it goes the default way. */
export function removeBranch(graph: WorkflowGraph, nodeId: string, index: number): WorkflowGraph {
  return editNext(graph, nodeId, (next) => ({
    ...next,
    branches: next.branches.filter((_, i) => i !== index),
  }));
}

/**
 * The endings no branch names: the ones the default arrow carries. An ending
 * that ends the call on purpose is not among them unless an arrow names it.
 */
export function defaultExits(node: WorkflowNode, exits: readonly StepExit[]): StepExit[] {
  const taken = new Set(nextOf(node).branches.flatMap((b) => b.outcomes));
  return exits.filter((e) => !taken.has(e.id) && !e.ends);
}

/** The endings that end the call here, because nothing is routed for them. */
export function endingExits(node: WorkflowNode, exits: readonly StepExit[]): StepExit[] {
  const taken = new Set(nextOf(node).branches.flatMap((b) => b.outcomes));
  return exits.filter((e) => e.ends && !taken.has(e.id));
}

/**
 * The label on a step's default arrow: nothing on a step with one way out,
 * the ending itself when only one is left, "Any other way" otherwise.
 */
export function otherwiseLabel(node: WorkflowNode, exits: readonly StepExit[]): string {
  if (nextOf(node).branches.length === 0) return '';
  const rest = defaultExits(node, exits);
  return rest.length === 1 ? rest[0]!.label : 'Any other way';
}

/** A label for a branch: its endings, in words. */
export function branchLabel(branch: Branch, exits: readonly StepExit[]): string {
  const byId = new Map(exits.map((e) => [e.id, e.label]));
  return branch.outcomes.map((o) => byId.get(o) ?? o).join(' or ');
}

// ── Where the call can go ────────────────────────────────────────────────────

/** Every step the call can reach from `from` along arrows, `from` included. */
export function reachFrom(graph: WorkflowGraph, from: string): Set<string> {
  const seen = new Set<string>();
  const stack = [from];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    const node = nodeOf(graph, id);
    if (!node) continue;
    seen.add(id);
    for (const t of node.transitions) stack.push(t.to);
  }
  return seen;
}

/** Whether an arrow from `from` to `to` keeps the call moving forward. */
export function canLeadTo(graph: WorkflowGraph, from: string, to: string): boolean {
  return from !== to && !reachFrom(graph, to).has(from);
}

/** Steps an arrow from `from` may point at: every step later in the call. */
export function laterSteps(graph: WorkflowGraph, from: string): WorkflowNode[] {
  // The steps that can lead to `from`, found by walking the arrows backwards
  // once rather than a walk forward from every step.
  const into = new Map<string, string[]>();
  for (const n of graph.nodes) {
    for (const t of n.transitions) {
      const list = into.get(t.to);
      if (list) list.push(n.id);
      else into.set(t.to, [n.id]);
    }
  }
  const earlier = new Set<string>();
  const stack = [from];
  while (stack.length) {
    const id = stack.pop()!;
    if (earlier.has(id)) continue;
    earlier.add(id);
    stack.push(...(into.get(id) ?? []));
  }
  return graph.nodes.filter((n) => !earlier.has(n.id));
}

/** Steps the call can never reach: from the start, the close, or an any-time rule. */
export function unreachable(graph: WorkflowGraph): Set<string> {
  const live = new Set<string>();
  const starts = [graph.entry, graph.close, graph.anyTime?.handoff, graph.anyTime?.callback];
  for (const start of starts) if (start) reachFrom(graph, start).forEach((id) => live.add(id));
  return new Set(graph.nodes.filter((n) => !live.has(n.id)).map((n) => n.id));
}

/** Steps in the order a call first meets them, then the ones it never reaches. */
export function walkOrder(graph: WorkflowGraph): WorkflowNode[] {
  const order: WorkflowNode[] = [];
  const seen = new Set<string>();
  const queue = [graph.entry];
  while (queue.length) {
    const node = nodeOf(graph, queue.shift()!);
    if (!node || seen.has(node.id)) continue;
    seen.add(node.id);
    order.push(node);
    queue.push(...node.transitions.map((t) => t.to));
  }
  return [...order, ...graph.nodes.filter((n) => !seen.has(n.id))];
}

// ── Adding and removing steps ────────────────────────────────────────────────

type NewNode = Pick<WorkflowNode, 'id' | 'stepId'>;

function withNode(graph: WorkflowGraph, node: NewNode, next: string | null): WorkflowGraph {
  return {
    ...graph,
    nodes: [
      ...graph.nodes,
      { id: node.id, stepId: node.stepId, transitions: next ? [{ to: next }] : [] },
    ],
  };
}

/** Put a step on the arrow from `from` to `to`: whatever went there goes through it. */
export function insertBetween(
  graph: WorkflowGraph,
  from: string,
  to: string,
  node: NewNode
): WorkflowGraph {
  const added = withNode(graph, node, to);
  return {
    ...added,
    nodes: added.nodes.map((n) =>
      n.id !== from
        ? n
        : { ...n, transitions: n.transitions.map((t) => (t.to === to ? { ...t, to: node.id } : t)) }
    ),
  };
}

/**
 * A new step at the end of one of `from`'s arrows (a branch, or its default).
 * It carries on to where `from`'s default goes, so a new branch rejoins the
 * call instead of ending it.
 */
export function insertAsTarget(
  graph: WorkflowGraph,
  from: string,
  which: number | 'otherwise' | { outcome: string },
  node: NewNode
): WorkflowGraph {
  const source = nodeOf(graph, from);
  if (!source) return graph;
  const rejoin = nextOf(source).otherwise;
  const added = withNode(graph, node, rejoin);
  if (which === 'otherwise') return setOtherwise(added, from, node.id);
  // One ending sent to the new step on an arrow of its own.
  if (typeof which === 'object') return addBranch(added, from, which.outcome, node.id);
  return setBranchTarget(added, from, which, node.id);
}

/**
 * A step that ends the call on purpose on some endings (a transfer that went
 * through) must not have a default: that would carry the call on past it. A
 * newly placed one keeps its arrow for its other endings only.
 */
export function withoutDefaultWhereEnding(
  graph: WorkflowGraph,
  nodeId: string,
  exits: readonly StepExit[]
): WorkflowGraph {
  const node = nodeOf(graph, nodeId);
  if (!node || !exits.some((e) => e.ends)) return graph;
  const next = nextOf(node);
  if (!next.otherwise) return graph;
  const rest = defaultExits(node, exits);
  return withNext(graph, nodeId, {
    branches: rest.length
      ? [...next.branches, { outcomes: rest.map((e) => e.id), to: next.otherwise }]
      : next.branches,
    otherwise: null,
  });
}

/**
 * Steps that would be left with no arrow at all if `nodeId` were removed:
 * it leads nowhere, so there is nothing to rejoin them to, and a step with no
 * arrow ends the call in silence.
 */
export function strandedByRemoving(graph: WorkflowGraph, nodeId: string): WorkflowNode[] {
  const after = removeNode(graph, nodeId);
  return graph.nodes.filter(
    (n) =>
      n.id !== nodeId &&
      n.transitions.some((t) => t.to === nodeId) &&
      (nodeOf(after, n.id)?.transitions.length ?? 0) === 0
  );
}

/** A new step straight after `after` on its default arrow, or on its own when null. */
export function insertAfter(
  graph: WorkflowGraph,
  after: string | null,
  node: NewNode
): WorkflowGraph {
  const source = after ? nodeOf(graph, after) : undefined;
  if (!source) return withNode(graph, node, null);
  const next = nextOf(source).otherwise;
  return setOtherwise(withNode(graph, node, next), source.id, node.id);
}

/**
 * Take a step out. Every arrow into it goes where its default went, so the
 * call carries on past the gap; with no default, those arrows go too.
 */
export function removeNode(graph: WorkflowGraph, nodeId: string): WorkflowGraph {
  const gone = nodeOf(graph, nodeId);
  if (!gone) return graph;
  const heir = nextOf(gone).otherwise ?? gone.transitions[0]?.to ?? null;
  const nodes = graph.nodes
    .filter((n) => n.id !== nodeId)
    .map((n) => {
      // Only the steps that pointed at it change. A step that has no default
      // on purpose (a transfer that went through ends the call) is left as
      // its author drew it.
      if (!n.transitions.some((t) => t.to === nodeId)) return n;
      const lostDefault = nextOf(n).otherwise === nodeId;
      const rejoined = {
        ...n,
        transitions: n.transitions.flatMap((t) =>
          t.to !== nodeId ? [t] : heir && heir !== n.id ? [{ ...t, to: heir }] : []
        ),
      };
      // Rejoining can leave a branch going exactly where the default does, or
      // two arrows to one step: fold them so every step keeps one default.
      return rebuilt(rejoined, tidy(nextOf(rejoined), lostDefault));
    });
  const next: WorkflowGraph = { ...graph, nodes };
  if (next.entry === nodeId) next.entry = heir ?? nodes[0]?.id ?? next.entry;
  if (next.close === nodeId) {
    delete next.close;
    delete next.closeWhen;
  }
  if (next.anyTime) {
    const anyTime = { ...next.anyTime };
    if (anyTime.handoff === nodeId) delete anyTime.handoff;
    if (anyTime.callback === nodeId) delete anyTime.callback;
    if (Object.keys(anyTime).length) next.anyTime = anyTime;
    else delete next.anyTime;
  }
  return next;
}

function tidy(next: NextSteps, lostDefault: boolean): NextSteps {
  const byTarget = new Map<string, string[]>();
  for (const b of next.branches) {
    if (b.to === next.otherwise) continue;
    byTarget.set(b.to, [...(byTarget.get(b.to) ?? []), ...b.outcomes]);
  }
  const branches = [...byTarget].map(([to, outcomes]) => ({
    to,
    outcomes: [...new Set(outcomes)],
  }));
  // A step that branches must keep a default, or any ending nobody named
  // would end the call there. When the default's step was the one removed,
  // the last branch becomes the default.
  if (lostDefault && next.otherwise === null && branches.length > 0) {
    const last = branches.pop()!;
    return { otherwise: last.to, branches };
  }
  return { otherwise: next.otherwise, branches };
}

/** A node id not yet used in the graph, readable in logs: `schedule`, `schedule_2`. */
export function newNodeId(graph: WorkflowGraph, componentKey: string): string {
  const base =
    componentKey
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_') || 'step';
  const used = new Set(graph.nodes.map((n) => n.id));
  if (!used.has(base)) return base;
  for (let i = 2; ; i++) if (!used.has(`${base}_${i}`)) return `${base}_${i}`;
}

// ── Where a step may run ────────────────────────────────────────────────────

const CHANNEL_NAMES: Record<ComponentChannel, string> = {
  voice: 'phone calls',
  sms: 'text messages',
  chat: 'web chat',
};

/** Why a component cannot go in this workflow, or null when it can. */
export function unavailableReason(
  component: AgentComponent,
  target: { channel: ComponentChannel; direction: ComponentCallDirection }
): string | null {
  const { meta } = component;
  if (!meta.channels.includes(target.channel)) {
    return `Works on ${meta.channels.map((c) => CHANNEL_NAMES[c]).join(' and ')} only`;
  }
  if (!meta.directions.includes(target.direction)) {
    return `Only on ${meta.directions.join(' and ')} workflows`;
  }
  return null;
}

/**
 * The engine runs builder workflows on phone calls only. The text-message
 * agent is a different engine, so a workflow on another channel can be
 * written and checked but never reaches anyone — the builder says so rather
 * than letting an author find out from silence.
 */
export function channelNotice(channel: ComponentChannel): string | null {
  if (channel === 'voice') return null;
  return `Nothing runs ${CHANNEL_NAMES[channel]} workflows built here yet. You can write and check this one, but it will not reach patients until that engine exists.`;
}

// ── Fields ─────────────────────────────────────────────────────────────────

/**
 * The fields to show for a step: its component's fields whose `when` holds,
 * without the ones only VagusX may change unless `ops` is on.
 */
export function visibleFields(
  fields: readonly ComponentField[],
  config: Record<string, unknown>,
  ops: boolean
): ComponentField[] {
  return fields.filter((f) => {
    if (f.ops && !ops) return false;
    if (f.when && configAt(config, f.when.path) !== f.when.equals) return false;
    return true;
  });
}

/**
 * The local line name a slot field stores its words under. A field that
 * already names one keeps it — a seeded step's lines are named after the slot
 * they came from, and renaming would orphan their words.
 */
export function lineName(
  config: Record<string, unknown>,
  path: string,
  defaultSlot?: string
): string {
  // A DOT path (`frontDesk.anythingElseSlot`), read like the engine reads it.
  const current = configAt(config, path);
  if (typeof current === 'string' && current.length > 0) return current;
  // A line the component reads by a built-in name keeps that name, as the
  // engine's compile does — otherwise the editor would show it empty while a
  // call speaks it.
  return defaultSlot ?? path;
}

/** The step's words for one line, as the editor shows them. */
export function lineWords(
  content: LineContent,
  config: Record<string, unknown>,
  path: string,
  defaultSlot?: string
): { en: string; es: string } {
  const words = content[lineName(config, path, defaultSlot)] ?? {};
  return { en: words.en ?? '', es: words.es ?? '' };
}

/**
 * Setting a line's words also names the line in the config, so the step says
 * which words it uses. Clearing both languages removes the line — an empty
 * line is "nothing to say", which the publish check reports.
 */
export function withLineWords(
  step: { config: Record<string, unknown>; content: LineContent },
  path: string,
  words: { en: string; es: string },
  defaultSlot?: string
): { config: Record<string, unknown>; content: LineContent } {
  const name = lineName(step.config, path, defaultSlot);
  const content = { ...step.content };
  const en = words.en.trim() ? words.en : undefined;
  const es = words.es.trim() ? words.es : undefined;
  if (!en && !es) {
    delete content[name];
    return { config: withConfigAt(step.config, path, undefined), content };
  }
  content[name] = { ...(en ? { en } : {}), ...(es ? { es } : {}) };
  return { config: withConfigAt(step.config, path, name), content };
}

// ── Problems ───────────────────────────────────────────────────────────────

export interface NodeProblems {
  errors: WorkflowProblem[];
  warnings: WorkflowProblem[];
}

/** Problems grouped by the node they are about; `''` holds whole-workflow ones. */
export function problemsByNode(problems: readonly WorkflowProblem[]): Map<string, NodeProblems> {
  const out = new Map<string, NodeProblems>();
  for (const p of problems) {
    const key = p.nodeId ?? '';
    const entry = out.get(key) ?? { errors: [], warnings: [] };
    (p.severity === 'warning' ? entry.warnings : entry.errors).push(p);
    out.set(key, entry);
  }
  return out;
}

/** The config field a problem is about: `config.contentSlot` → `contentSlot`. */
export function problemField(problem: WorkflowProblem): string | null {
  if (!problem.at) return null;
  const match = /^config\.([A-Za-z0-9_]+)/.exec(problem.at);
  return match ? match[1]! : null;
}

// ── Facts, as a person reads them ──────────────────────────────────────────

/**
 * A gate fact in words. Most are a component's own record (`identityVerified`,
 * `booking`) and read well enough as they are; the ones the engine writes for
 * every line (`delivered.<key>`) do not.
 */
export function factLabel(fact: string): string {
  const heard = /^delivered\.(.+)\.heard$/.exec(fact);
  if (heard) return `The line “${heard[1]}” was heard`;
  const said = /^delivered\.(.+)$/.exec(fact);
  if (said) return `The line “${said[1]}” was said`;
  return fact;
}

/**
 * A line with each `{{fact}}` replaced by its example, for the preview under
 * the editor. Display only — the call renders with its own engine.
 */
export function previewLine(text: string, examples: ReadonlyMap<string, string>): string {
  // `{{#if x}}…{{else}}…{{/if}}` and `{{#unless x}}…{{/unless}}`, one level
  // deep: a fact with an example counts as present.
  const blocks = text.replace(
    /\{\{#(if|unless)\s+([A-Za-z0-9_.]+)\s*\}\}([\s\S]*?)(?:\{\{else\}\}([\s\S]*?))?\{\{\/\1\}\}/g,
    (_whole, kind: string, path: string, yes: string, no: string | undefined) => {
      const present = examples.has(path) === (kind === 'if');
      return present ? yes : (no ?? '');
    }
  );
  return blocks.replace(/\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g, (_whole, path: string) => {
    return examples.get(path) ?? `[${path}]`;
  });
}
